/* eslint-disable jsx-a11y/role-supports-aria-props */
import { LogOut, X } from "lucide-react";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Leftbar({ sidebar, toggleSidebar }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 🔥 ALL MENU (Single Source of Truth)
  const ALL_MENU = [
    {
      title: "Dashboard",
      key: "dashboard",
      icon: "ti ti-home",
      path: "/dashboard",
      roles: ["admin"],
    },
    {
      title: "Lead Dashboard",
      key: "leadDashboard",
      icon: "ti ti-dashboard",
      path: "/lead-dashboard",
      roles: ["admin", "leadmanager"],
    },
    {
      title: "Lead Management",
      key: "leads",
      icon: "ti ti-briefcase",
      roles: ["admin", "leadmanager"],
      children: [
        { name: "Add Lead", path: "/add-lead" },
        { name: "Leads List", path: "/leads" },
      ],
    },
    {
      title: "Inquiry Management",
      key: "inquiry",
      icon: "ti ti-help",
      roles: ["admin", "leadmanager"],
      children: [{ name: "Inquiries", path: "/inquiries" }],
    },
    {
      title: "Startups",
      key: "startup",
      icon: "ti ti-building",
      roles: ["admin"],
      children: [{ name: "Startup List", path: "/startups" }],
    },
    {
      title: "Incubation Center",
      key: "incubation",
      icon: "ti ti-currency-dollar",
      roles: ["admin"],
      children: [
        { name: "Add Incubation Center", path: "/incubation/add" },
        { name: "List Incubation Center", path: "/incubation/list" },
      ],
    },
    {
      title: "Communication",
      key: "comm",
      icon: "ti ti-message",
      roles: ["admin", "leadmanager"],
      children: [{ name: "Bulk Message", path: "/bulk-message" }],
    },
    {
      title: "Investors",
      key: "investor",
      icon: "ti ti-currency-dollar",
      roles: ["admin"],
      children: [
        { name: "Add Investor", path: "/addinvestor" },
        { name: "Investor List", path: "/investorlist" },
      ],
    },
    {
      title: "Mentor",
      key: "mentor",
      icon: "ti ti-user",
      roles: ["admin"],
      children: [
        { name: "Add Mentor", path: "/add-mentor" },
        { name: "Mentor List", path: "/mentor-list" },
      ],
    },
    {
      title: "Accountants",
      key: "ca",
      icon: "ti ti-users",
      roles: ["admin"],
      children: [{ name: "Add CA", path: "/add-ca" }],
    },
    {
      title: "Tech Company",
      key: "tech",
      icon: "ti ti-cpu",
      roles: ["admin"],
      children: [{ name: "Add Tech Company", path: "/add-tech-company" }],
    },
    {
      title: "Partners",
      key: "partner",
      icon: "ti ti-handshake",
      roles: ["admin"],
      children: [{ name: "Add Partner", path: "/add-partner" }],
    },
    {
      title: "Courses",
      key: "courses",
      icon: "ti ti-book",
      roles: ["admin"],
      children: [
        { name: "Add Course", path: "/add-course" },
        { name: "Add Module", path: "/add-module" },
        { name: "Upload Video", path: "/upload-video" },
      ],
    },
    {
      title: "Templates",
      key: "template",
      icon: "ti ti-clipboard",
      roles: ["admin"],
      children: [
        { name: "Add Category", path: "/add-category" },
        { name: "Add Template", path: "/add-template" },
        { name: "Template List", path: "/template-list" },
      ],
    },
  ];

  // Filtered menu based on roles with safety check
  const filteredMenu =
    user?.role === "admin"
      ? ALL_MENU
      : ALL_MENU.filter((m) => m.roles?.includes(user?.role));

  // Find active dropdown based on current URL path on initial load / reload
  const getActiveDropdownKey = () => {
    for (const menu of ALL_MENU) {
      if (menu.children) {
        const found = menu.children.some((child) => child.path === location.pathname);
        if (found) return menu.key;
      }
    }
    return null;
  };

  const [openDropdown, setOpenDropdown] = useState(getActiveDropdownKey);

  // Keep dropdown open if the current path belongs to it even after re-renders/reloads
  useEffect(() => {
    const activeKey = getActiveDropdownKey();
    if (activeKey) {
      setOpenDropdown(activeKey);
    }
  }, [location.pathname]);

  const toggleDropdown = (key) => {
    setOpenDropdown(openDropdown === key ? null : key);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
      <aside className={`left-sidebar ${sidebar ? "active" : ""}`}>
        <div className="sidebar-container">
          {/* Logo Section */}
          <div className="brand-logo d-flex align-items-center justify-content-between px-4">
            <Link to="/dashboard" className="text-decoration-none d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center fw-bold text-white shadow-sm"
                style={{
                  width: "40px",
                  height: "40px",
                  background: "linear-gradient(135deg,#435ebe,#6b8bef)",
                  borderRadius: "12px",
                  fontSize: "1.2rem",
                }}
              >
                IM
              </div>
              <span className="fw-bold text-dark" style={{ fontSize: "1.1rem", letterSpacing: "0.5px" }}>
                Admin Portal
              </span>
            </Link>

            {/* Close Button: Mobile Only */}
            <button
              className="btn d-xl-none text-dark border-0 p-0"
              onClick={toggleSidebar}
              aria-label="Close Sidebar"
            >
              <X size={24} />
            </button>
          </div>

          {/* Nav Links: Scrollable Area */}
          <nav className="sidebar-nav flex-grow-1">
            <ul className="list-unstyled px-3">
              {filteredMenu.map((menu, i) => {
                if (!menu.children) {
                  return (
                    <li key={i}>
                      <Link
                        to={menu.path}
                        className={`sidebar-link ${location.pathname === menu.path ? "active" : ""}`}
                        onClick={() => window.innerWidth < 1200 && toggleSidebar()}
                      >
                        <i className={menu.icon}></i>
                        <span className="ms-2">{menu.title}</span>
                      </Link>
                    </li>
                  );
                }

                return (
                  <li key={i}>
                    <div
                      className={`sidebar-link ${openDropdown === menu.key ? "active" : ""}`}
                      onClick={() => toggleDropdown(menu.key)}
                      style={{ cursor: "pointer" }}
                    >
                      <i className={menu.icon}></i>
                      <span className="ms-2">{menu.title}</span>
                      <i
                        className={`ti ti-chevron-down ms-auto arrow-icon ${openDropdown === menu.key ? "rotate" : ""
                          }`}
                      ></i>
                    </div>

                    <ul className={`submenu list-unstyled ${openDropdown === menu.key ? "show" : ""}`}>
                      {menu.children.map((child, j) => (
                        <li key={j}>
                          <Link
                            to={child.path}
                            className={`submenu-link ${location.pathname === child.path ? "active" : ""}`}
                            onClick={() => window.innerWidth < 1200 && toggleSidebar()}
                          >
                            {child.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Profile/Logout: Pinned to Bottom */}
          <div className="profile-box border-top">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column">
                <small className="text-muted" style={{ fontSize: "10px", fontWeight: "700" }}>
                  LOGGED IN AS
                </small>
                <span className="text-dark text-capitalize fw-bold" style={{ fontSize: "13px" }}>
                  {user?.role || "User"}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="logout-btn"
                title="Logout"
                aria-label="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}